import React from "react";
import { Form, Input, Button, Typography, Flex, theme, type FormInstance } from "antd";
import { CheckCircleFilled, TagOutlined, BarcodeOutlined } from "@ant-design/icons";
import SectionHeader from "../../shared/components/SectionHeader";
import type { ConceptData } from "../types";

const { Text } = Typography;

const FieldLabel: React.FC<{ text: string }> = ({ text }) => {
  const { token } = theme.useToken();
  return (
    <Text
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: token.colorTextSecondary,
      }}
    >
      {text}
    </Text>
  );
};

const inputStyle = (token: ReturnType<typeof theme.useToken>["token"]): React.CSSProperties => ({
  borderRadius: token.borderRadiusLG,
  background: token.colorBgContainer,
  border: `1px solid ${token.colorBorderSecondary}`,
  fontSize: 14,
  fontWeight: 500,
});

interface ConceptFormProps {
  form: FormInstance<ConceptData>;
  onFinish: (values: ConceptData) => void;
  isPending: boolean;
  buttonText: string;
  formId?: string;
}

const ConceptForm: React.FC<ConceptFormProps> = ({ form, onFinish, isPending, buttonText, formId = "concept-form" }) => {
  const { token } = theme.useToken();

  return (
    <Form
      id={formId}
      form={form}
      layout="vertical"
      onFinish={onFinish}
      requiredMark={false}
      style={{ width: "100%" }}
    >
      <Flex vertical gap={16} style={{ marginBottom: 28 }}>
        <SectionHeader title="Información del Concepto" />

        <Form.Item
          name="concept_name"
          label={<FieldLabel text="Nombre del Concepto" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            prefix={<TagOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. Asesoría Financiera"
            style={inputStyle(token)}
          />
        </Form.Item>

        <Form.Item
          name="clave_sat"
          label={<FieldLabel text="Clave SAT" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            prefix={<BarcodeOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. 84111506"
            style={inputStyle(token)}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={<FieldLabel text="Descripción" />}
          style={{ marginBottom: 0 }}
        >
          <Input.TextArea
            size="large"
            style={inputStyle(token)}
            rows={4}
          />
        </Form.Item>
      </Flex>

      <Button
        type="primary"
        htmlType="submit"
        size="large"
        loading={isPending}
        icon={<CheckCircleFilled />}
        block
        style={{
          height: 54,
          borderRadius: token.borderRadiusLG,
          fontWeight: 800,
          fontSize: 16,
          letterSpacing: "0.01em",
          boxShadow: `0 8px 20px ${token.colorPrimaryBg}`,
        }}
      >
        {buttonText}
      </Button>
    </Form>
  );
};

export default ConceptForm;
